import React, { memo, useMemo, useState, useEffect, useCallback } from 'react'
import classnames from 'classnames'
import { Drawer } from '@pushly/aqe/lib/components'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../_utils/object'
import { INotificationFieldSuggestions } from '../../../../interfaces/notification-field-suggestions'
import { FieldSuggestionResultsManagerProps, SuggestedFieldsForm } from './types'
import { extractFormSelections } from './helpers'
import PageAnalyzerResults from './components/page-analyzer-results'
import PushlyAiResults from './components/pushly-ai-results'
import { ErrorBoundary } from './components/error-boundary'
import '../../styles/notification-field-suggestions.scss'

/**
 * FieldSuggestionResultsManager - Main container component
 *
 * Manages the overall state and props interface, determines which child component
 * to render based on result source, handles drawer visibility and submission logic,
 * and maintains backward compatibility with existing prop interface.
 */
export const FieldSuggestionResultsManager = memo((props: FieldSuggestionResultsManagerProps) => {
    const { domain, results, onClose, onAccept } = props
    const [form] = useForm<SuggestedFieldsForm>()
    const [selectedKeywords, setSelectedKeywords] = useState<string[] | null>(null)

    // Track which variant is selected to replace the current (only one allowed)
    const [replaceVariant, setReplaceVariant] = useState<{
        variantIndex: number
        selections: Partial<INotificationFieldSuggestions>
    } | null>(null)

    // Track which variants are selected to add as new variants
    const [addVariants, setAddVariants] = useState<
        Record<
            number,
            {
                selected: boolean
                selections: Partial<INotificationFieldSuggestions>
            }
        >
    >({})

    // Get current variant data (for non-AI sources)
    const currentVariantData = useMemo(() => {
        if (!results?.data || results.data.length === 0) return null
        return results.data[0] // For page analyzer, just use the first (and only) result
    }, [results?.data])

    // Initialize selected keywords for page analyzer
    const suggestedKws = useMemo<string[]>(() => {
        return currentVariantData ? extract(currentVariantData, 'keywords', []) : ([] as string[])
    }, [currentVariantData?.keywords])

    const isAiSource = results?.source === 'pushly_ai'

    useEffect(() => {
        const hasKwSuggestions = suggestedKws.length > 0

        if (!isAiSource && hasKwSuggestions && selectedKeywords === null) {
            setSelectedKeywords(suggestedKws)
        }
    }, [isAiSource, suggestedKws, selectedKeywords])

    // Handle replace variant selection (only one allowed) - memoized for performance
    const handleReplaceSelectionChange = useCallback(
        (variantIndex: number, selected: boolean, selections: Partial<INotificationFieldSuggestions>) => {
            if (selected) {
                setReplaceVariant({
                    variantIndex,
                    selections,
                })
                // If this variant is selected for replace, remove it from add selections
                setAddVariants((prev) => ({
                    ...prev,
                    [variantIndex]: {
                        selected: false,
                        selections: prev[variantIndex]?.selections || {},
                    },
                }))
            } else {
                setReplaceVariant(null)
            }
        },
        [],
    )

    // Handle add variant selection (multiple allowed) - memoized for performance
    const handleAddSelectionChange = useCallback(
        (variantIndex: number, selected: boolean, selections: Partial<INotificationFieldSuggestions>) => {
            if (selected) {
                // If this variant is selected for add, remove it from replace selection
                if (replaceVariant?.variantIndex === variantIndex) {
                    setReplaceVariant(null)
                }
            }

            setAddVariants((prev) => ({
                ...prev,
                [variantIndex]: {
                    selected,
                    selections,
                },
            }))
        },
        [replaceVariant?.variantIndex],
    )

    // Determine if suggestions are empty
    const suggestionsAreEmpty =
        results === null ||
        !results.data ||
        results.data.length === 0 ||
        results.data.every((variant) => {
            return Object.values(variant).every((v) => v === null || v === undefined)
        })

    // Generate submit text based on source and selections
    const submitText = useMemo(() => {
        if (suggestionsAreEmpty) {
            return 'Go Back'
        }

        if (isAiSource) {
            const hasReplace = replaceVariant !== null
            const addCount = Object.values(addVariants).filter((v) => v.selected).length
            const totalSelections = (hasReplace ? 1 : 0) + addCount
            return totalSelections > 1 ? 'Accept Selections' : 'Accept Selection'
        }

        return 'Accept Selections'
    }, [suggestionsAreEmpty, isAiSource, replaceVariant, addVariants])

    // Handle drawer submission - memoized for performance
    const handleSubmit = useCallback(async () => {
        try {
            if (isAiSource) {
                const hasReplaceSelection = replaceVariant !== null
                const addVariantsList = Object.entries(addVariants)
                    .filter(([, data]) => data.selected)
                    .map(([, data]) => data.selections)

                if (!hasReplaceSelection && addVariantsList.length === 0) {
                    return // No variants selected
                }

                await onAccept?.(replaceVariant?.selections ?? null, addVariantsList)
            } else {
                // For page analyzer, use form data to build selections
                const values = form.getFieldsValue()
                const selections = extractFormSelections(values, currentVariantData!, domain, selectedKeywords)
                await onAccept?.(selections)
            }
        } catch (error) {
            console.error('Error submitting field suggestions:', error)
            // Error handling could be enhanced with user notification
        }
    }, [isAiSource, replaceVariant, addVariants, onAccept, form, currentVariantData, domain, selectedKeywords])

    return (
        <Drawer
            className={classnames('notification-field-suggestions-drawer', `source-${results?.source ?? 'unknown'}`)}
            title={isAiSource ? 'Pushly AI Generated Results' : 'Page Analysis Results'}
            open={results !== null}
            maskClosable={true}
            destroyOnClose={true}
            onClose={onClose}
            onSubmit={handleSubmit}
            submitText={submitText}
        >
            {results !== null && (
                <ErrorBoundary>
                    <div className="pre-form-details">
                        {isAiSource ? (
                            <PushlyAiResults
                                domain={domain}
                                results={results.data}
                                builder={props.builder}
                                replaceVariant={replaceVariant}
                                addVariants={addVariants}
                                onReplaceSelectionChange={handleReplaceSelectionChange}
                                onAddSelectionChange={handleAddSelectionChange}
                            />
                        ) : (
                            <PageAnalyzerResults
                                domain={domain}
                                currentVariantData={currentVariantData!}
                                form={form}
                                selectedKeywords={selectedKeywords}
                                setSelectedKeywords={setSelectedKeywords}
                            />
                        )}
                    </div>
                </ErrorBoundary>
            )}
        </Drawer>
    )
})

FieldSuggestionResultsManager.displayName = 'FieldSuggestionResultsManager'
